/**
 * How a playbook run's evidence is read: what counts as a change, what is
 * ordinary table activity, and when a test got worse. Shared by the run page
 * and the Word export so both say the same thing.
 */
/** A dataset's starting-versus-final comparison, as the server reports it. */
export type DatasetComparison = { device_id: string; dataset: string; status: string; reason?: string; added: unknown[]; removed: unknown[]; before_at?: string; after_at?: string; volatile_changed?: boolean; hidden_values_changed?: boolean; truncated?: boolean };
export type Measured = { samples?: number; lost?: number; average_ms?: number; p95_ms?: number; metrics?: Record<string, number>; flows?: Record<string, unknown>[]; summary?: string; paths?: unknown };
export type BehaviorResult = { step_id: string; kind?: string; status: string; before?: Measured; after?: Measured; reason?: string; delta?: Record<string, number>; paths_changed?: boolean; path_reason?: string };
export type EvidenceStep = { id: string; name: string; kind: string; device_id?: string; device_ids?: string[] };

const DATASET: Record<string, string> = { facts: 'Facts', running_config: 'Running config', interfaces: 'Interfaces', routes: 'Routes', vrfs: 'VRFs', mac: 'MAC table', arp: 'ARP', lldp: 'LLDP', cdp: 'CDP', policies: 'Policies' };
export const label = (d: string) => DATASET[d] ?? d.replaceAll('_', ' ');
export type Tone = 'ok' | 'changed' | 'unknown' | 'bad' | 'churn';
// MAC and ARP entries are learned and aged out all the time: a host appearing
// on a port is activity, shown but not counted as the device changing.
const CHURN = new Set(['mac', 'arp']);
export const toneOf = (status: string, dataset = ''): Tone => status === 'unchanged' ? 'ok' : status === 'changed' ? (CHURN.has(dataset) ? 'churn' : 'changed') : status === 'failed' || status === 'collection_failed' ? 'bad' : 'unknown';
export const WORD: Record<Tone, string> = { ok: 'Unchanged', changed: 'Changed', unknown: 'Inconclusive', bad: 'Collection failed', churn: 'Table activity' };

/** One record in a line: its identifying fields first, nested values skipped. */
export function summarize(row: unknown): string {
  if (row === null || typeof row !== 'object') return String(row);
  const o = row as Record<string, unknown>;
  const first = ['name', 'interface', 'prefix', 'mac', 'address', 'ip', 'vlan', 'neighbor', 'hostname', 'device_id'];
  const keys = [...first.filter(k => k in o), ...Object.keys(o).filter(k => !first.includes(k))];
  const parts: string[] = [];
  for (const k of keys) {
    const v = o[k];
    if (v === null || v === undefined || v === '' || typeof v === 'object' && !Array.isArray(v) || k === 'entry_type' && v === 'DYNAMIC') continue;
    const text = Array.isArray(v) ? v.filter(x => typeof x !== 'object').join(', ') : String(v);
    if (text) parts.push(first.slice(0, 6).includes(k) ? text : `${k.replaceAll('_', ' ')} ${text}`);
    if (parts.length >= 5) break;
  }
  return parts.join(' · ');
}

/** For one record that changed in place (facts), the fields that differ: field: old → new. */
export function fieldChanges(before: unknown, after: unknown, prefix = ''): string[] {
  if (before === null || after === null || typeof before !== 'object' || typeof after !== 'object' || Array.isArray(before) || Array.isArray(after)) {
    // Lists of plain values (a MAC's ports) read as one value.
    const show = (v: unknown) => Array.isArray(v) && v.every(x => typeof x !== 'object') ? v.join(', ') || 'none' : JSON.stringify(v);
    return JSON.stringify(before) === JSON.stringify(after) ? [] : [`${(prefix || 'value').replaceAll('_', ' ')}: ${show(before)} → ${show(after)}`];
  }
  const a = before as Record<string, unknown>, b = after as Record<string, unknown>, out: string[] = [];
  for (const k of new Set([...Object.keys(a), ...Object.keys(b)])) out.push(...fieldChanges(a[k], b[k], prefix ? `${prefix}.${k}` : k));
  return out;
}

/** What a record is, by its identifying field: an interface's name, a route's prefix, a MAC address. */
export function identity(row: unknown): string | undefined {
  if (row === null || typeof row !== 'object') return undefined;
  const o = row as Record<string, unknown>;
  const key = ['name', 'interface', 'prefix', 'mac', 'address', 'ip', 'hostname'].find(k => typeof o[k] === 'string' && o[k]);
  return key && `${key}=${String(o[key])}`;
}

/** Key test metrics, starting versus final, judged worse, better or about the same. */
export const METRICS: [string, string, (m: Measured | undefined) => number | undefined, 'ms' | '%'][] = [
  // A ping is a burst of probes and fails only when none come back, so a
  // target that rate-limits ICMP drops probes without any ping failing: the
  // two are reported apart rather than packet loss standing in for both.
  ['loss', 'Failed pings', m => m?.samples ? (100 * (m.lost ?? 0)) / m.samples : m?.metrics?.loss_pct, '%'],
  ['packet_loss', 'Packet loss', m => m?.samples ? m.metrics?.loss_pct : undefined, '%'],
  ['avg', 'Average RTT', m => m?.average_ms ?? m?.metrics?.rtt_avg_ms, 'ms'],
  ['p95', 'p95 RTT', m => m?.p95_ms ?? m?.metrics?.rtt_p95_ms, 'ms'],
  ['jitter', 'Jitter', m => m?.metrics?.jitter_ms, 'ms'],
];
export function judge(key: string, before?: number, after?: number): Tone {
  if (before === undefined || after === undefined) return 'unknown';
  const d = after - before;
  if (key === 'loss' || key === 'packet_loss') return d > 0.5 ? 'bad' : 'ok';
  // Latency: a few milliseconds or under 20% is ordinary variation.
  if (d > Math.max(5, before * 0.5)) return 'bad';
  if (d > Math.max(1, before * 0.2)) return 'changed';
  return 'ok';
}
/** A traffic step's application flows, in a phrase. */
export const flows = (m?: Measured) => m?.flows ? `${m.flows.length} application flow${m.flows.length === 1 ? '' : 's'}${m.summary ? ` · ${m.summary}` : ''}` : undefined;


/** The record a field change belongs to, as a reader names it: a MAC address and its VLAN, an interface. */
function subject(row: unknown): string {
  const o = row as Record<string, unknown>;
  const id = identity(row)?.split('=').slice(1).join('=') ?? '';
  return o.vlan !== undefined && o.vlan !== '' && o.vlan !== null ? `${id} (VLAN ${String(o.vlan)})` : id;
}

/**
 * Whether records changed in place (facts, an interface's settings, a MAC
 * that moved port) rather than some replaced others: every added record has a
 * removed one with the same identity. Each line names its record.
 */
export function inPlace(c: DatasetComparison): string[] | null {
  if (c.dataset === 'facts') return c.added.length === 1 && c.removed.length === 1 ? fieldChanges(c.removed[0], c.added[0]) : null;
  if (c.added.length === 0 || c.added.length !== c.removed.length) return null;
  const key = (r: unknown) => `${identity(r)}|${String((r as Record<string, unknown>).vlan ?? '')}`;
  const before = new Map(c.removed.map(r => [key(r), r]));
  if (before.size !== c.removed.length || c.added.some(r => identity(r) === undefined || !before.has(key(r)))) return null;
  return c.added.flatMap(r => fieldChanges(before.get(key(r)), r).map(line => `${subject(r)} — ${line}`));
}

/** One line of a configuration diff: unchanged context, added or removed. */
export type DiffLine = { kind: ' ' | '+' | '-'; text: string };
/** A run of changes with context, and the section it falls in. */
export type DiffHunk = { section?: string; lines: DiffLine[] };

// Lines IOS rewrites on its own, which say nothing about the change.
const VOLATILE_LINE = /^(Building configuration\.\.\.|Current configuration : \d+ bytes|! Last configuration change at .+|! NVRAM config last updated at .+|ntp clock-period \d+)$/;
const configLines = (text: string) => text.replace(/\r/g, '').split('\n').filter((l) => !VOLATILE_LINE.test(l.trim()));

/** The shortest edit from a to b (Myers), as kept, added and removed lines. */
function myers(a: string[], b: string[]): DiffLine[] {
  const n = a.length, m = b.length, max = n + m, offset = max + 1;
  const v = new Int32Array(2 * max + 3), trace: Int32Array[] = [];
  let found = false;
  for (let d = 0; d <= max && !found; d++) {
    trace.push(v.slice());
    for (let k = -d; k <= d; k += 2) {
      let x = k === -d || (k !== d && v[offset + k - 1]! < v[offset + k + 1]!) ? v[offset + k + 1]! : v[offset + k - 1]! + 1;
      let y = x - k;
      while (x < n && y < m && a[x] === b[y]) { x++; y++; }
      v[offset + k] = x;
      if (x >= n && y >= m) { found = true; break; }
    }
    // Configurations that share almost nothing are shown as replaced.
    if (d > 4000) return [...a.map((text) => ({ kind: '-' as const, text })), ...b.map((text) => ({ kind: '+' as const, text }))];
  }
  const out: DiffLine[] = [];
  let x = n, y = m;
  for (let d = trace.length - 1; d >= 0 && (x > 0 || y > 0); d--) {
    const vd = trace[d]!, k = x - y;
    const prevK = k === -d || (k !== d && vd[offset + k - 1]! < vd[offset + k + 1]!) ? k + 1 : k - 1;
    const prevX = d === 0 ? 0 : vd[offset + prevK]!, prevY = prevX - prevK;
    while (x > prevX && y > prevY) out.push({ kind: ' ', text: a[--x]! }), y--;
    if (d > 0) { if (x === prevX) out.push({ kind: '+', text: b[--y]! }); else out.push({ kind: '-', text: a[--x]! }); }
  }
  return out.reverse();
}

/**
 * What changed between two configurations: only the changed lines, with
 * `context` unchanged lines around each, grouped into hunks labelled with the
 * section they fall in (the interface, router or other block above them).
 */
export function configDiff(before: string, after: string, context = 3): DiffHunk[] {
  const ops = myers(configLines(before), configLines(after));
  const changed = ops.map((o) => o.kind !== ' ');
  const keep = ops.map((_, i) => changed.slice(Math.max(0, i - context), i + context + 1).some(Boolean));
  // The section each line falls in: the last top-level line above it.
  const sections: (string | undefined)[] = [];
  let section: string | undefined;
  for (const o of ops) {
    sections.push(section);
    if (o.text !== '' && !o.text.startsWith(' ') && o.text !== '!' && o.kind !== '+') section = o.text;
  }
  const hunks: DiffHunk[] = [];
  let current: DiffHunk | null = null;
  ops.forEach((o, i) => {
    if (!keep[i]) { current = null; return; }
    if (!current) { current = { lines: [] }; hunks.push(current); }
    // Labelled by the section its first change is in, when that is a setting inside one.
    if (current.section === undefined && o.kind !== ' ' && o.text.startsWith(' ')) current.section = sections[i];
    current.lines.push(o);
  });
  return hunks;
}

/** A configuration dataset's change, when it is one: the old and new text. */
export function configTexts(c: DatasetComparison): [string, string] | null {
  const pick = (rows: unknown[]) => rows.length === 1 ? (typeof rows[0] === 'string' ? rows[0] : typeof (rows[0] as { text?: unknown })?.text === 'string' ? (rows[0] as { text: string }).text : null) : null;
  if (c.dataset !== 'running_config') return null;
  const before = pick(c.removed), after = pick(c.added);
  return before !== null && after !== null ? [before, after] : null;
}
